import {EditorView, WidgetType} from '@codemirror/view'
import type {Element} from 'hast'
import {toDom} from 'hast-util-to-dom'

import {nodesEqual} from '../../language-tools/html/hast-utils'

/**
 * Widget that renders a hast node.
 *
 * Typically used to display elements that have no editable child content and must be rendered as complete replacement
 * decorations rather than wrapping decorations.
 */
export class HastElementWidget extends WidgetType {
  #node: Element
  #className: string | undefined

  /**
   * @param node - hast node to render.
   * @param className - Optional CSS class name to apply in addition to node.properties.class
   */
  constructor(node: Element, className?: string) {
    super()
    this.#node = node
    this.#className = className
  }

  override toDOM(_view: EditorView) {
    const result = toDom(this.#node)
    if (!(result instanceof HTMLElement)) throw new Error('HastElementWidget encountered non-element render result')
    pruneWhitespaceTextNodes(result)
    const dom: HTMLElement = result
    dom.style.marginBlock = '0'
    if (this.#className) {
      dom.classList.add(this.#className)
    }
    return dom
  }

  override eq(widget: WidgetType): boolean {
    return (
      widget instanceof HastElementWidget &&
      nodesEqual(this.#node, widget.#node, {ignorePosition: true}) &&
      widget.#className === this.#className
    )
  }

  /**
   * Allow events to bubble so that CodeMirror can handle them natively.
   */
  override ignoreEvent() {
    return false
  }
}

/**
 * Remove whitespace-only text nodes from a rendered DOM subtree.
 *
 * Hast-to-DOM conversion may produce text nodes that only contain formatting whitespace.
 * These nodes cause visible blank lines inside widgets, so this routine walks the tree and
 * deletes them in-place before the widget is inserted into the editor.
 *
 * @param root - Root element of the DOM subtree to normalize. The subtree is mutated.
 */
function pruneWhitespaceTextNodes(root: HTMLElement) {
  const stack: ChildNode[] = Array.from(root.childNodes)

  while (stack.length > 0) {
    const node = stack.pop()!

    if (node.nodeType === Node.TEXT_NODE) {
      if ((node.textContent ?? '').trim() === '') {
        node.remove()
      }
      continue
    }

    if (node instanceof Element) {
      stack.push(...Array.from(node.childNodes))
    }
  }
}
